import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ChatCitation } from './ChatCitation';

/**
 * Citations list wrapper component
 * Displays multiple citations with a header
 *
 * Memoized to prevent unnecessary re-renders
 */
export const ChatCitationsList = memo(function ChatCitationsList({ citations }) {
  const { t } = useTranslation();

  // Deduplicate citations based on URL and snippet to prevent React key warnings
  // Multiple citations can reference the same URL with different snippets
  const uniqueCitations = useMemo(() => {
    if (!citations || citations.length === 0) return [];

    const seen = new Map();
    return citations.filter(citation => {
      // Create unique key from URL and first 50 chars of snippet
      const snippetPreview = citation.snippet?.substring(0, 50) || '';
      const key = `${citation.url || 'unknown'}-${snippetPreview}`;

      if (seen.has(key)) return false;
      seen.set(key, true);
      return true;
    });
  }, [citations]);

  if (!uniqueCitations || uniqueCitations.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 pt-4 border-t border-border-default">
      <p className="text-xs text-muted mb-2">
        {uniqueCitations.length === 1 ? t('chat.citation.source') : t('chat.citation.sources')} (
        {uniqueCitations.length}):
      </p>
      {uniqueCitations.map((citation, index) => (
        <ChatCitation
          key={`${citation.url || 'unknown'}-${index}`}
          citation={citation}
          index={index}
          totalCount={uniqueCitations.length}
        />
      ))}
    </div>
  );
});

ChatCitationsList.propTypes = {
  citations: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      file: PropTypes.string,
      title: PropTypes.string,
      source_type: PropTypes.string,
      snippet: PropTypes.string,
      score: PropTypes.number,
    })
  ),
};
